/**
 * The tasks an assignment asks for, in the brief's own shape (2026-10-07).
 *
 * > Owner: *"use our little gateway and retrieve the tasks and assign a weight to each one,
 * > default is split among them … for now a converter, this can be on the brief"* — then, on the
 * > first extraction: *"we should be able to put the summary above … sub tasks are optional,
 * > sometimes there isn't and the requirements stay on the top task!"*
 *
 * 🔴 THREE LEVELS. What the assignment is (context, never marked); tasks, each with a description
 * and the optional / extra-credit switches; and the subtasks a task may have, each with a
 * description and its own requirements. A task with no subtasks holds its requirements itself.
 * 🔴 THE GRADER ANSWERS EACH REQUIREMENT AND THE CONSOLE ADDS UP C1. A description is what a
 * requirement is read against; it is never answered.
 * 🔴 FIND IS A PROPOSAL. One call to the gateway model reads the brief; what it finds lands in the
 * draft, and nothing is stored until the save bar's own Save, whose preview says what re-grades.
 * ⚠️ A WEIGHT IS A SHARE, ON WHATEVER HOLDS THE REQUIREMENTS: each subtask, or a task with none.
 * The points beside it are what it comes to against C1 today — `tasks.ts`, the server's
 * arithmetic mirrored and gated by the same vectors.
 */
import { useId, useState } from "react";
import { type ApiError, extractTasks, type SubtaskDraft, type TaskDraft } from "../api";
import { evenWeights, subtasksOf, taskPoints, withIds } from "../tasks";
import { tracked } from "../useAsync";
import { Badge, ErrorBox, Why } from "../ui";

/** What C1 is worth on this assignment as it is configured on screen. */
export interface TaskHost { label: string; mode: "score" | "advisory" | "off"; points: number }

const fmt = (n: number) => (Math.round(n * 100) / 100).toString();
/** Enough rows for every line as it wraps, so none is hidden under the next. ⚠️ An estimate at
 *  about 64 characters a line; `field-sizing: content` does it exactly where a browser has it. */
const rowsFor = (rs: string[], min = 2) =>
  Math.min(12, Math.max(min, rs.reduce((n, r) => n + Math.max(1, Math.ceil(r.length / 64)), 0)));
const BLANK_TASK: TaskDraft = { title: "", description: "", requirements: [""], optional: false,
                                extra_credit: false, weight: 1, subtasks: [] };
const BLANK_SUB: SubtaskDraft = { title: "", description: "", requirements: [""], weight: 1 };

/** A proposal's tasks without the ids the server numbered them with — they are new here. */
function fresh(ts: TaskDraft[]): TaskDraft[] {
  return ts.map(({ id: _t, ...t }) => ({
    ...t, subtasks: subtasksOf(t).map(({ id: _s, ...s }) => s) }));
}

export function AssignmentTasks({ courseId, cw, tasks, saved, summary, host, hasBrief, readOnly,
                                  onChange, onSummary }: {
  courseId: string; cw: string;
  tasks: TaskDraft[]; saved: TaskDraft[];
  summary: string;
  host: TaskHost;
  hasBrief: boolean;
  readOnly: boolean;
  onChange: (t: TaskDraft[]) => void;
  onSummary: (s: string) => void;
}) {
  const ided = withIds(tasks, saved);
  const pts = taskPoints(ided, host.points);
  const unitPts = new Map(pts.units.map((u) => [u.id, u]));
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState<{ model: string; note: string; n: number } | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  const uid = useId();

  const set = (i: number, patch: Partial<TaskDraft>) =>
    onChange(tasks.map((t, j) => (i === j ? { ...t, ...patch } : t)));
  const setSub = (i: number, k: number, patch: Partial<SubtaskDraft>) =>
    set(i, { subtasks: subtasksOf(tasks[i]!).map((s, j) => (j === k ? { ...s, ...patch } : s)) });
  const find = () => {
    setFinding(true); setErr(null);
    tracked(extractTasks(courseId, cw))
      // ⚠️ THE PROPOSAL'S IDS ARE DROPPED: they are new tasks, and the server gives them numbers
      // after the highest this assignment has used. Keeping them would let a proposed T1 take
      // over a saved T1 that asks for something else.
      .then((p) => {
        onChange(fresh(p.tasks));
        if (p.summary) onSummary(p.summary);
        setFound({ model: p.model, note: p.note, n: p.tasks.length });
      })
      .catch((e) => setErr(e as ApiError))
      .finally(() => setFinding(false));
  };
  // ⚠️ ONLY THE WEIGHTS ARE TAKEN BACK from the split, never the provisional ids it numbered with.
  const even = () => {
    const done = evenWeights(ided, host.points);
    onChange(tasks.map((t, i) => {
      const d = done[i]!;
      const subs = subtasksOf(t);
      return subs.length
        ? { ...t, subtasks: subs.map((s, k) => ({ ...s, weight: d.subtasks[k]?.weight ?? s.weight })) }
        : { ...t, weight: d.weight ?? t.weight };
    }));
  };
  const add = () => onChange([...tasks, { ...BLANK_TASK, requirements: [""], subtasks: [] }]);
  const remove = (i: number) => onChange(tasks.filter((_, j) => j !== i));
  /** 🔴 A TASK'S FIRST SUBTASK TAKES ITS REQUIREMENTS AND WEIGHT, so nothing typed is lost: a task
   *  with subtasks holds no requirements of its own. */
  const addSub = (i: number) => {
    const t = tasks[i]!;
    const subs = subtasksOf(t);
    if (subs.length) {
      set(i, { subtasks: [...subs, { ...BLANK_SUB, requirements: [""] }] });
      return;
    }
    const own = t.requirements.filter((r) => r.trim());
    const first: SubtaskDraft[] = own.length
      ? [{ title: t.title, description: "", requirements: t.requirements, weight: t.weight ?? 1 }]
      : [];
    const { weight: _w, ...rest } = t;
    onChange(tasks.map((x, j) => (j === i
      ? { ...rest, requirements: [], subtasks: [...first, { ...BLANK_SUB, requirements: [""] }] }
      : x)));
  };
  /** ⚠️ REMOVING THE LAST SUBTASK GIVES THE TASK ITS REQUIREMENTS BACK, because a task with
   *  neither could never earn; Discard undoes it like anything else. */
  const removeSub = (i: number, k: number) => {
    const subs = subtasksOf(tasks[i]!);
    const left = subs.filter((_, j) => j !== k);
    if (left.length) { set(i, { subtasks: left }); return; }
    const gone = subs[k]!;
    set(i, { subtasks: [], requirements: gone.requirements.length ? gone.requirements : [""],
             weight: gone.weight });
  };

  const requiredTasks = ided.filter((t) => !t.optional);
  const requiredUnits = pts.units.filter((u) => u.counts === "required");
  const nOpt = ided.length - requiredTasks.length;
  const off = host.mode === "off";
  const ptsLabel = (counts: string, p: number | undefined) =>
    counts === "report_only" ? "reported" : `= ${p ?? 0} pts`;

  /** The requirements box: what holds a weight, a task with none or a subtask. */
  // 🔴 THE TWO BOXES LOOK ALIKE AND MEAN OPPOSITE THINGS — one is read, the other is marked — so
  // each says which above it. [X] Seen in a render: without the captions nothing told them apart.
  // ⚠️ The captions are for the eye; each box's accessible name is its own `aria-label`.
  const reqBox = (id: string, reqs: string[], onReqs: (r: string[]) => void) => (<>
    <span className="tk-cap" aria-hidden="true">Requirements · each one marked met, partly or
      missing</span>
    {/* ⚠️ THE BOX KEEPS WHAT IS TYPED, blank lines included: the lines are split, not filtered,
        until the save tidies them — or Enter at the end of a line is undone. */}
    <textarea className="ta prose tk-reqs" rows={rowsFor(reqs)} value={reqs.join("\n")}
              disabled={readOnly} aria-label={`Requirements of ${id}, one per line`}
              placeholder="One per line, as the brief lists them: a single thing a grader can answer met, partly met or missing."
              onChange={(e) => onReqs(e.target.value.split("\n"))} />
  </>);
  const descBox = (id: string, text: string, onText: (s: string) => void, what: string) => (<>
    <span className="tk-cap" aria-hidden="true">Description · read by the grader, not marked</span>
    <textarea className="ta prose tk-desc" rows={rowsFor([text], 1)} value={text}
              disabled={readOnly} aria-label={`Description of ${id}`}
              placeholder={`What this ${what} asks for.`}
              onChange={(e) => onText(e.target.value)} />
  </>);
  const weightBox = (id: string, w: number | undefined, counts: string, onW: (n: number) => void,
                     zero: boolean) => (
    // ⚠️ THE INPUT IS NAMED ON ITS OWN: a label wrapping the points beside it made its accessible
    // name "Weight of T1= 24 pts".
    <span className="tk-w">
      <input className="input" type="number" min={1} max={1000} step={1} value={String(w ?? 1)}
             disabled={readOnly} aria-label={`Weight of ${id}`}
             onChange={(e) => onW(Math.round(Number(e.target.value)))} />
      <span className={`tk-pts${zero ? " bad" : ""}`}>{ptsLabel(counts, unitPts.get(id)?.points)}</span>
    </span>);
  const each = (id: string, n: number) =>
    n ? `${n} requirement${n === 1 ? "" : "s"}, ${fmt((unitPts.get(id)?.points ?? 0) / n)} points each`
      : "No requirements yet.";

  return (
    <section className="as-notes tk" aria-labelledby={`${uid}-h`}>
      <div className="as-sec-head">
        <h4 className="as-sec-t" id={`${uid}-h`}>Tasks</h4>
        <span className="as-sec-m">
          {off ? <>split <b>{host.label}</b>, which is off here</>
            : <>split the {host.points} points of <b>{host.label}</b>
                {host.mode === "advisory" && " (advisory: judged, counts nothing)"}</>}
        </span>
        <Why label="How tasks decide the mark">
          The grader answers each requirement met, partly met or missing, citing its evidence. The
          console adds up the points: a requirement is worth its share of what holds it — a
          subtask, or a task with none — and partly met is half. Summaries and descriptions are
          what the grader reads the requirements against; they are never marked. An optional task is
          judged and worth nothing, unless it counts as extra credit — then it can make up points
          lost on required tasks, never past the criterion's total. Changing the tasks re-grades this
          assignment's marks; changing the summary does not.
        </Why>
        {tasks.length > 0 && (
          <span className="as-sec-aside">
            <button className="btn sm ghost" type="button"
                    disabled={readOnly || requiredUnits.length === 0} onClick={even}>Split evenly</button>
            <button className="btn sm ghost" type="button" disabled={readOnly || finding || !hasBrief}
                    onClick={find}>{finding ? "Reading the brief…" : "Find again"}</button>
          </span>)}
      </div>
      {err && <ErrorBox err={err} />}
      {off && tasks.length > 0 && (
        <p className="small warn">{host.label} is off on this assignment, so these tasks are not
          sent to the grader. Turn it back on under Evaluation.</p>)}

      {(tasks.length > 0 || summary) && (
        <label className="as-field tk-summary" htmlFor={`${uid}-sum`}>What the assignment is
          <textarea id={`${uid}-sum`} className="ta prose tk-desc" rows={rowsFor([summary], 2)}
                    value={summary} disabled={readOnly}
                    placeholder="The brief's own opening, in a sentence or two. The grader reads it; it is not marked."
                    onChange={(e) => onSummary(e.target.value)} /></label>)}

      {tasks.length === 0 ? (
        <div className="tk-empty">
          <p className="small">No tasks yet. Without them the grader decides on every run what
            the assignment asked and how much each part is worth.</p>
          <div className="row gap">
            <button className="btn primary sm" type="button"
                    disabled={readOnly || finding || !hasBrief || off} onClick={find}>
              {finding ? "Reading the brief…" : "Find the tasks in the brief"}</button>
            <button className="btn sm ghost" type="button" disabled={readOnly || off}
                    onClick={add}>Write them yourself</button>
          </div>
          <p className="as-hint">{!hasBrief
            ? "Classroom has no description for this assignment, so there is nothing to read."
            : "One call to the gateway model, a few seconds. It proposes; nothing is saved until you save."}</p>
        </div>
      ) : (<>
        {found && (
          <p className="as-hint tk-found" role="status">
            <span>Found in the brief by <b>{found.model}</b>: {found.n} task{found.n === 1 ? "" : "s"}.
              Check them before saving.</span>
            {found.note && <span>{found.note}</span>}
          </p>)}
        <ol className="tk-list">
          {ided.map((t, i) => {
            const draft = tasks[i]!;
            const counts = !t.optional ? "required" : t.extra_credit ? "extra_credit" : "report_only";
            const subs = t.subtasks;
            const nReqs = subs.length
              ? subs.reduce((n, s) => n + s.requirements.filter((r) => r.trim()).length, 0)
              : t.requirements.filter((r) => r.trim()).length;
            return (
              // ⚠️ `tk-opt`, NOT `opt`: `.opt` is the option card elsewhere in the stylesheet, and
              // its `input { grid-row: span 3 }` pulled this card's fields into a column.
              <li key={i} className={`tk-task${t.optional ? " tk-opt" : ""}`}>
                <div className="tk-top">
                  <code className="tk-id">{t.id}</code>
                  <input className="input tk-title" type="text" value={draft.title} disabled={readOnly}
                         maxLength={160} aria-label={`Name of task ${t.id}`}
                         placeholder="What this task asks for"
                         onChange={(e) => set(i, { title: e.target.value })} />
                  {subs.length
                    ? <span className="tk-w"><span className="tk-pts">
                        {ptsLabel(counts, pts.byTask.get(t.id))}</span></span>
                    : weightBox(t.id, draft.weight, counts, (w) => set(i, { weight: w }),
                                pts.zero.includes(t.id))}
                  <button className="btn sm ghost" type="button" disabled={readOnly}
                          aria-label={`Remove ${t.id}`} onClick={() => remove(i)}>✕</button>
                </div>
                {descBox(t.id, draft.description ?? "", (d) => set(i, { description: d }), "task")}
                {subs.length === 0
                  ? reqBox(t.id, draft.requirements, (r) => set(i, { requirements: r }))
                  : (
                    <ol className="tk-subs" aria-label={`Subtasks of ${t.id}`}>
                      {subs.map((s, k) => {
                        const sd = subtasksOf(draft)[k]!;
                        const n = s.requirements.filter((r) => r.trim()).length;
                        return (
                          <li key={k} className="tk-sub">
                            <div className="tk-top">
                              <code className="tk-id">{s.id}</code>
                              <input className="input tk-title" type="text" value={sd.title}
                                     disabled={readOnly} maxLength={160}
                                     aria-label={`Name of subtask ${s.id}`}
                                     placeholder="What this subtask asks for"
                                     onChange={(e) => setSub(i, k, { title: e.target.value })} />
                              {weightBox(s.id, sd.weight, counts, (w) => setSub(i, k, { weight: w }),
                                         pts.zero.includes(s.id))}
                              <button className="btn sm ghost" type="button" disabled={readOnly}
                                      aria-label={`Remove ${s.id}`}
                                      onClick={() => removeSub(i, k)}>✕</button>
                            </div>
                            {descBox(s.id, sd.description ?? "",
                                     (d) => setSub(i, k, { description: d }), "subtask")}
                            {reqBox(s.id, sd.requirements, (r) => setSub(i, k, { requirements: r }))}
                            <div className="as-foot tk-foot"><span>{counts === "report_only"
                              ? "Judged and reported, worth nothing." : each(s.id, n)}</span></div>
                          </li>);
                      })}
                    </ol>)}
                <div className="as-foot tk-foot">
                  <Badge tone={t.optional ? "warn" : "ok"}>{t.optional ? "optional" : "required"}</Badge>
                  <label className="as-check" htmlFor={`${uid}-o${i}`}>
                    <input id={`${uid}-o${i}`} type="checkbox" checked={t.optional} disabled={readOnly}
                           onChange={(e) => set(i, { optional: e.target.checked,
                                                     extra_credit: e.target.checked && t.extra_credit })} />
                    Optional</label>
                  {t.optional && (
                    <label className="as-check" htmlFor={`${uid}-x${i}`}>
                      <input id={`${uid}-x${i}`} type="checkbox" checked={t.extra_credit}
                             disabled={readOnly}
                             onChange={(e) => set(i, { extra_credit: e.target.checked })} />
                      Counts as extra credit, up to {pts.byTask.get(t.id) ?? 0}</label>)}
                  <span>
                    {subs.length
                      ? `${subs.length} subtask${subs.length === 1 ? "" : "s"} · ${nReqs} requirement${nReqs === 1 ? "" : "s"}`
                      : counts === "report_only" ? "Judged and reported, worth nothing."
                      : each(t.id, nReqs)}</span>
                  <span className="spacer" />
                  <button className="btn sm ghost" type="button" disabled={readOnly}
                          aria-label={`Add a subtask to ${t.id}`}
                          onClick={() => addSub(i)}>+ Subtask</button>
                </div>
              </li>);
          })}
        </ol>
        <div className="row gap">
          <button className="btn sm" type="button" disabled={readOnly} onClick={add}>+ Add a task</button>
        </div>
        {requiredTasks.length === 0 ? (
          <p className="alert small refused" role="alert">Every task is optional, so nothing would
            decide what the assignment asked. Make at least one required.</p>
        ) : pts.zero.length > 0 ? (
          <p className="alert small refused" role="alert">
            {pts.zero.join(", ")} come{pts.zero.length === 1 ? "s" : ""} to 0 points, so it could
            never earn. Give it more weight, or the others less.</p>
        ) : (
          <div className="pts-split">
            <div className="pts-bar" aria-hidden="true">
              {requiredUnits.map((u) => (
                <i key={u.id} style={{ width: `${(u.points / Math.max(1, host.points)) * 100}%` }} />))}
            </div>
            <div className="pts-key">
              <span>{requiredTasks.length} required{nOpt ? ` · ${nOpt} optional` : ""}</span>
              {pts.requiredWeight !== host.points && (
                <span>weights add up to {pts.requiredWeight}, scaled to {host.points}</span>)}
              {requiredUnits.map((u) => <span key={u.id}><b>{u.id}</b> {u.points}</span>)}
            </div>
          </div>)}
      </>)}
    </section>
  );
}


